/**
 * Wem gehoert eine Pause der Tagesaufnahme? (30.10.2026)
 *
 * Die Tagesaufnahme stand oft auf „Pause", obwohl niemand gedrueckt hatte,
 * und die Wache meldete „fortgesetzt", ohne etwas zu tun. Die Reparatur
 * braucht eine Wahrheit, die eine Pause nicht selbst mitbringt: WER sie
 * wollte. Diese Datei haelt sie fest und ueberlebt den Neustart der Seite.
 *
 *   nutzer  Martin hat Pause gedrueckt. Wird NIE automatisch aufgehoben.
 *   system  Wir haben pausiert, mit Grund (Wiedergabe, Sprechtaste, Anruf).
 *   fremd   Jemand anderes (iOS, Unterbrechung) — ohne Grund, ohne Nutzer.
 *
 * ⚠️ Fehlt der Eintrag bei einer Pause, gilt sie als fremd. Der Nutzer
 *    traegt sich IMMER ein, bevor der Befehl hinausgeht.
 */

export type PauseArt = "nutzer" | "system" | "fremd";

export interface PauseBesitzer {
  art: PauseArt;
  grund: string;
  seit: number;
}

export const BESITZER_KEY = "voice-v8.pauseBesitzer";
export const NEUBEGINN_KEY = "voice-v8.aufnahmeNeubeginn";
export const ZULETZT_LIEF_KEY = "voice-v8.aufnahmeZuletztLief";
/** So lange nach dem Eintrag glaubt ein „laeuft" dem Nutzer-Eintrag nicht. */
export const SCHONFRIST_MS = 5_000;
/** Hoechstens so oft meldet eine unfreiwillige Pause per Ton/Vibration. */
export const WECKRUF_ABSTAND_MS = 60_000;
/** Wie lange ein Neubeginn angezeigt wird. */
export const NEUBEGINN_ANZEIGE_MS = 2 * 3_600_000;

let imSpeicher: PauseBesitzer | null = null;
let letzterWeckruf = 0;
/** 60 s; bei dauerhaft scheiterndem Fortsetzen (nach dem Alarm) 5 Min. */
let weckAbstand = WECKRUF_ABSTAND_MS;
export const WECKRUF_ABSTAND_NACH_ALARM_MS = 5 * 60_000;

export function setzeWeckAbstand(nachAlarm: boolean): void {
  weckAbstand = nachAlarm ? WECKRUF_ABSTAND_NACH_ALARM_MS : WECKRUF_ABSTAND_MS;
}

function lies(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function schreibe(key: string, wert: string | null): boolean {
  try {
    if (wert === null) localStorage.removeItem(key);
    else localStorage.setItem(key, wert);
    return true;
  } catch {
    /* Quota/Privat-Modus — der Speicher im Prozess gilt weiter */
    return false;
  }
}

/** Das Schreiben ging schief: dann gilt der Merker im Prozess. */
let nurImSpeicher = false;

function gueltig(b: unknown): b is PauseBesitzer {
  const o = b as PauseBesitzer | null;
  return (
    !!o &&
    (o.art === "nutzer" || o.art === "system" || o.art === "fremd") &&
    typeof o.seit === "number" &&
    typeof o.grund === "string"
  );
}

export function leseBesitzer(): PauseBesitzer | null {
  let roh: string | null;
  try {
    roh = localStorage.getItem(BESITZER_KEY);
  } catch {
    /* kein Speicher: dann gilt der Merker im Prozess */
    return imSpeicher;
  }
  if (!roh) return nurImSpeicher ? imSpeicher : null;
  try {
    const b: unknown = JSON.parse(roh);
    if (gueltig(b)) return b;
  } catch {
    /* kaputter Eintrag zaehlt als keiner */
  }
  return null;
}

export function merkeBesitzer(art: PauseArt, grund = "", jetzt = Date.now()): PauseBesitzer {
  const b: PauseBesitzer = {
    art,
    grund: grund.replace(/[^a-z0-9-]/gi, "").slice(0, 48),
    seit: jetzt,
  };
  imSpeicher = b;
  nurImSpeicher = !schreibe(BESITZER_KEY, JSON.stringify(b));
  return b;
}

/**
 * Einen Besitzer eintragen, ohne einen Nutzer zu verdraengen. Ein Nutzer
 * bleibt Besitzer, bis er selbst fortsetzt oder die Pause endet.
 */
export function merkeBesitzerSchwach(art: PauseArt, grund = "", jetzt = Date.now()): void {
  const b = leseBesitzer();
  if (b?.art === "nutzer") return;
  if (b && art === "fremd") return;
  merkeBesitzer(art, grund, jetzt);
}

export function vergissBesitzer(): void {
  imSpeicher = null;
  nurImSpeicher = false;
  schreibe(BESITZER_KEY, null);
  schreibe(NATIV_GRUND_KEY, null);
  schreibe(PAUSEN_START_KEY, null);
}

/**
 * Der letzte Grund, den die native Engine bei einer Pause mitschickte
 * (`grund` im recorderState-Ereignis). Er liegt im Speicher, damit er den
 * Neustart der Seite ueberlebt.
 *
 * ⚠️ EHRLICHE GRENZE: Nach einem Neustart der Seite liefert `getState`
 *    den Grund NICHT mit (PawbotRecorderPlugin.swift ~393-396), und das
 *    Ereignis kommt nur beim Wechsel. Eine Pause, deren Grund wir nie
 *    gesehen haben, ist darum „unbekannt" — und wird NICHT von selbst
 *    aufgehoben, nur angezeigt.
 */
export const NATIV_GRUND_KEY = "voice-v8.pauseNativGrund";

export function leseNativenGrund(): string {
  return lies(NATIV_GRUND_KEY) ?? "";
}

/** Gruende, die eine Pause erklaeren und die die Wache beheben darf. */
export function istBelegteUrsache(grund: string): boolean {
  return (
    grund === "wiedergabe" ||
    grund.startsWith("unterbrechung") ||
    grund === "mikro-verweigert" ||
    grund === "medien-neustart" ||
    grund === "haeppchen-abbruch"
  );
}

/**
 * Die Engine hat „pausiert" gemeldet, mit oder ohne Grund.
 *   widget  Martin am Widget / in der Live-Activity: gehoert ihm.
 *   seite   Befehl dieser Seite: ohne anderen Besitzer ebenfalls seiner
 *           (unser Wiedergabe-Halt tragt sich VORHER als „system" ein).
 */
export function meldePauseGrund(grund: string | undefined, jetzt = Date.now()): void {
  const g = (grund ?? "").replace(/[^a-z0-9-]/gi, "").slice(0, 48);
  if (!g) return;
  schreibe(NATIV_GRUND_KEY, g);
  if (g === "widget") merkeBesitzer("nutzer", "widget", jetzt);
  else if (g === "seite" && !leseBesitzer()) merkeBesitzer("nutzer", "seite", jetzt);
}

export const PAUSEN_START_KEY = "voice-v8.pauseNativSeit";

/**
 * Der Abgleich kennt den Beginn der Pause. Aendert er sich, ist die alte
 * Pause vorbei und ein gemerkter Grund gehoert zu ihr, nicht zur neuen —
 * ein veraltetes „wiedergabe" darf eine spaetere Widget-Pause nicht freigeben.
 * Nennt der Abgleich den Grund selbst, gilt er (meldePauseGrund).
 */
export function meldePausenStart(pausedAtMs: number, grundVomAbgleich?: string): void {
  if (!(pausedAtMs > 0)) return;
  const alt = Number(lies(PAUSEN_START_KEY));
  schreibe(PAUSEN_START_KEY, String(pausedAtMs));
  if (alt > 0 && alt !== pausedAtMs && !grundVomAbgleich) schreibe(NATIV_GRUND_KEY, null);
}

export type WacheUrteil = "nutzer" | "belegt" | "unbekannt";

/**
 * Darf die Wache eine Pause ohne offene eigene Gruende fortsetzen?
 *   nutzer    nie.
 *   belegt    eine Ursache ist bekannt (oder wir haben selbst pausiert).
 *   unbekannt nein — nur anzeigen.
 */
export function urteilFuerWache(): WacheUrteil {
  const b = leseBesitzer();
  if (b?.art === "nutzer") return "nutzer";
  if (b?.art === "system") return "belegt";
  return istBelegteUrsache(leseNativenGrund()) ? "belegt" : "unbekannt";
}

export function istNutzerPause(): boolean {
  return leseBesitzer()?.art === "nutzer";
}

/**
 * Der Zustand hat gewechselt: Pausen-Eintraege raeumen, wenn nichts mehr
 * pausiert ist; bei einer Pause ohne Eintrag „fremd" eintragen.
 */
export function gleicheMitZustandAb(
  zustand: string,
  /** true beim Abgleich (kann veraltet sein), false bei einem Ereignis der Engine. */
  mitSchonfrist = true,
  jetzt = Date.now(),
): void {
  /* Pause ohne Eintrag: gilt als fremd — dafuer braucht es keinen Vermerk. */
  if (zustand === "paused") return;
  const b = leseBesitzer();
  if (mitSchonfrist && b && jetzt - b.seit < SCHONFRIST_MS) return;
  vergissBesitzer();
}

export function merkeLaeuft(jetzt = Date.now()): void {
  schreibe(ZULETZT_LIEF_KEY, String(jetzt));
}

export interface Neubeginn {
  um: number;
  lueckeMin: number;
}

/** Eine neue Sitzung wurde ohne Zutun des Nutzers begonnen. */
export function leseZuletztLief(): number {
  const v = Number(lies(ZULETZT_LIEF_KEY));
  return Number.isFinite(v) ? v : 0;
}

/**
 * `vorher`: wann es zuletzt lief — VOR dem neuen Start gelesen, denn der
 * Start selbst schreibt „laeuft" und liesse die Luecke auf 0 fallen.
 */
export function merkeNeubeginn(vorher = leseZuletztLief(), jetzt = Date.now()): Neubeginn {
  const luecke = Number.isFinite(vorher) && vorher > 0 ? Math.max(0, jetzt - vorher) : 0;
  const n: Neubeginn = { um: jetzt, lueckeMin: Math.round(luecke / 60_000) };
  schreibe(NEUBEGINN_KEY, JSON.stringify(n));
  return n;
}

export function leseNeubeginn(jetzt = Date.now()): Neubeginn | null {
  const roh = lies(NEUBEGINN_KEY);
  if (!roh) return null;
  try {
    const n = JSON.parse(roh) as Neubeginn;
    if (typeof n.um !== "number" || typeof n.lueckeMin !== "number") return null;
    if (jetzt - n.um > NEUBEGINN_ANZEIGE_MS || n.um > jetzt) return null;
    return n;
  } catch {
    return null;
  }
}

export type PauseAnzeige = "von-dir" | "wartet" | "grund" | "unbekannt";
export const TEXT_UNBEKANNT = "Pausiert – Grund unbekannt, tippen zum Fortsetzen";

/** Welche der ehrlichen Anzeigen gilt fuer eine Pause. */
export function pauseAnzeige(offeneGruende: readonly string[]): PauseAnzeige {
  const b = leseBesitzer();
  if (b?.art === "nutzer") return "von-dir";
  if (offeneGruende.length > 0 || leseNativenGrund() === "wiedergabe") return "grund";
  // Die Wache fasst eine Pause ohne belegte Ursache nicht an: ehrlich sagen.
  if (urteilFuerWache() === "unbekannt") return "unbekannt";
  return "wartet";
}

export const TEXT_VON_DIR = "Pausiert (von dir)";
export const TEXT_WARTET = "Wartet auf Fortsetzen – tippen";

function zwei(n: number): string {
  return String(n).padStart(2, "0");
}

export function neubeginnText(n: Neubeginn): string {
  const d = new Date(n.um);
  return `Aufnahme neu begonnen um ${zwei(d.getHours())}:${zwei(d.getMinutes())}, Lücke ${n.lueckeMin} Min`;
}

/** Der Satz fuer eine Pause. `grundText` gilt, wenn ein eigener Grund haelt. */
export function pauseText(offeneGruende: readonly string[], grundText: string): string {
  const a = pauseAnzeige(offeneGruende);
  if (a === "von-dir") return TEXT_VON_DIR;
  if (a === "wartet") return TEXT_WARTET;
  if (a === "unbekannt") return TEXT_UNBEKANNT;
  return grundText;
}

/** Der Lauf-Text, mit dem Neubeginn dahinter, solange er gilt. */
export function laufText(basis: string, jetzt = Date.now()): string {
  const n = leseNeubeginn(jetzt);
  return n ? `${basis} · ${neubeginnText(n)}` : basis;
}

/** Ist es Zeit fuer Ton/Vibration? Hoechstens alle 60 s. */
export function darfWecken(jetzt = Date.now()): boolean {
  if (jetzt - letzterWeckruf < weckAbstand) return false;
  letzterWeckruf = jetzt;
  return true;
}

/** Nur fuer Tests: die Seite wurde neu geladen, der Speicher bleibt. */
export function _seiteNeuGeladen(): void {
  imSpeicher = null;
  letzterWeckruf = 0;
}

/** Nur fuer Tests. */
export function _besitzerVergessen(): void {
  imSpeicher = null;
  letzterWeckruf = 0;
  nurImSpeicher = false;
  schreibe(BESITZER_KEY, null);
  schreibe(NATIV_GRUND_KEY, null);
  schreibe(PAUSEN_START_KEY, null);
  schreibe(NEUBEGINN_KEY, null);
  weckAbstand = WECKRUF_ABSTAND_MS;
  schreibe(ZULETZT_LIEF_KEY, null);
}
